// /profile/privacy — account privacy, notification switches, blocked users and
// account deletion. An account page (pages/account/kit.js); in the app it's the
// native Privacy screen, whose back closes it ("privacyBack").
import { Navigate, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BellRing, EyeOff, Lock, SlidersHorizontal, Trash2, VolumeX } from "lucide-react";
import authAxios from "../authAxios";
import { useGlobalState } from "../GlobalStates";
import AccountDeletionModal from "../components/common/AccountDeletionModal";
import BlockedUsersList from "./Connections/BlockedUsersList";
import { Badge, Button, Section, Skeleton } from "../pages/manage/ui";
import { clubToast } from "../pages/manage/toast";
import { AccountPage, SettingsGroup, SettingsRow, isDedicatedAppScreen, postToApp } from "../pages/account/kit";

const Privacy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useGlobalState("user");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [blockedCount, setBlockedCount] = useState(null);

  const handleToggle = async (setting) => {
    if (setting === "privateAccount") {
      setUser((prev) => ({
        ...prev,
        is_private: !prev.is_private,
      }));

      try {
        await authAxios.post("/v1/social/updatePrivateAccount", {
          privateAccount: !user.is_private,
        });
        clubToast.success(t("private_account_success", {
            defaultValue: "Private account setting updated successfully",
          }));
      } catch (error) {
        console.error("Error updating private account:", error);
        // Revert on error
        setUser((prev) => ({
          ...prev,
          is_private: !prev.is_private,
        }));
        clubToast.error(t("private_account_error", {
            defaultValue: "Failed to update private account setting",
          }));
      }
    } else if (setting === "hideActivity") {
      setUser((prev) => ({
        ...prev,
        hide_social_activity: !prev.hide_social_activity,
      }));

      try {
        await authAxios.post("/v1/social/updateHideActivity", {
          hideActivity: !user.hide_social_activity,
        });
        clubToast.success(t("activity_visibility_success", {
            defaultValue: "Activity visibility updated successfully",
          }));
      } catch (error) {
        console.error("Error updating hide activity:", error);
        // Revert on error
        setUser((prev) => ({
          ...prev,
          hide_social_activity: !prev.hide_social_activity,
        }));
        clubToast.error(t("activity_visibility_error", {
            defaultValue: "Failed to update activity visibility",
          }));
      }
    } else if (setting === "pushNotifications") {
      setUser((prev) => ({
        ...prev,
        allow_social_push_notifications: !prev.allow_social_push_notifications,
      }));

      try {
        await authAxios.post("/v1/social/updatePushNotifications", {
          allowPushNotifications: !user.allow_social_push_notifications,
        });
        clubToast.success(t("push_notifications_success", {
            defaultValue: "Push notification settings updated successfully",
          }));
      } catch (error) {
        console.error("Error updating push notifications:", error);
        // Revert on error
        setUser((prev) => ({
          ...prev,
          allow_social_push_notifications: !prev.allow_social_push_notifications,
        }));
        clubToast.error(t("push_notifications_error", {
            defaultValue: "Failed to update push notification settings",
          }));
      }
    } else if (setting === "muteAllNotifications") {
      const currentMuteStatus = user.mute_all_notifications ?? false;
      setUser((prev) => ({
        ...prev,
        mute_all_notifications: !currentMuteStatus,
      }));

      try {
        await authAxios.post("/v1/social/updateMuteAllNotifications", {
          muteAllNotifications: !currentMuteStatus,
        });
        clubToast.success(t("mute_all_notifications_success", {
            defaultValue: "Mute all notifications setting updated successfully",
          }));
      } catch (error) {
        console.error("Error updating mute all notifications:", error);
        // Revert on error
        setUser((prev) => ({
          ...prev,
          mute_all_notifications: !prev.mute_all_notifications,
        }));
        clubToast.error(t("mute_all_notifications_error", {
            defaultValue: "Failed to update mute all notifications setting",
          }));
        // Revert on error
        setUser((prev) => ({
          ...prev,
          mute_all_notifications: currentMuteStatus,
        }));
      }
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);

    try {
      await authAxios.post("/v1/user/deleteAccount");

      clubToast.success(t("account_deletion_success", {
          defaultValue: "Your account has been successfully deleted",
        }));

      // Clear user data and redirect
      setTimeout(() => {
        // Clear localStorage items (following logout cleanup pattern)
        localStorage.removeItem("USER_ID");
        localStorage.removeItem("JWT_TOKEN");
        localStorage.removeItem("locale");
        localStorage.removeItem("currency");
        localStorage.removeItem("community");

        // Clear user state
        setUser({});

        // Redirect to home page
        window.location.href = "/";
      }, 2000);
    } catch (error) {
      console.error("Error deleting account:", error);
      clubToast.error(t("account_deletion_error", {
          defaultValue: "Failed to delete account. Please try again.",
        }));
    } finally {
      setIsDeletingAccount(false);
      setShowDeleteModal(false);
    }
  };

  const [userLoading] = useGlobalState("userLoading");

  const page = (children) => (
    <AccountPage
      title={t("privacy", { defaultValue: "Privacy" })}
      description={t("privacy_description", {
        defaultValue: "Manage your privacy preferences and control how others interact with your account.",
      })}
      back={{ fallback: "/profile", appMessage: "privacyBack" }}
    >
      {children}
    </AccountPage>
  );

  if (userLoading) {
    return page(
      <div className="tw-flex tw-flex-col tw-gap-3">
        <Skeleton className="tw-h-32 tw-w-full" />
        <Skeleton className="tw-h-44 tw-w-full" />
      </div>
    );
  }

  if (!user.id) {
    return <Navigate to="/" />;
  }

  // The native Privacy screen opens Notifications as a native screen too.
  const openNotificationPrefs = () => {
    if (isDedicatedAppScreen()) postToApp("navigateToNotifEdit");
    else navigate("/notif-edit");
  };

  return page(
    <>
      <Section first title={t("account_privacy_section", { defaultValue: "Account privacy" })}>
        <SettingsGroup>
          <SettingsRow
            icon={Lock}
            label={t("account_private_account", { defaultValue: "Private account" })}
            description={t("private_account_description", { defaultValue: "Only approved followers can see your content" })}
            checked={user.is_private}
            onCheckedChange={() => handleToggle("privateAccount")}
          />
          <SettingsRow
            icon={EyeOff}
            label={t("hide_activity", { defaultValue: "Hide activity" })}
            description={t("hide_activity_description", { defaultValue: "Hide your activity from your followers" })}
            checked={user.hide_social_activity}
            onCheckedChange={() => handleToggle("hideActivity")}
          />
        </SettingsGroup>
      </Section>

      <Section title={t("notifications", { defaultValue: "Notifications" })}>
        <SettingsGroup>
          <SettingsRow
            icon={BellRing}
            label={t("account_push_notifications", { defaultValue: "Push notifications" })}
            description={t("push_notifications_description", { defaultValue: "Receive push notifications for new updates to your social network" })}
            checked={user.allow_social_push_notifications}
            onCheckedChange={() => handleToggle("pushNotifications")}
          />
          <SettingsRow
            icon={VolumeX}
            label={t("account_mute_all", { defaultValue: "Mute all notifications" })}
            description={t("mute_all_notifications_description", {
              defaultValue: "Temporarily disable all notifications. You won't receive any notifications while this is enabled.",
            })}
            checked={user.mute_all_notifications ?? false}
            onCheckedChange={() => handleToggle("muteAllNotifications")}
          />
          <SettingsRow
            icon={SlidersHorizontal}
            label={t("account_notification_prefs", { defaultValue: "Notification preferences" })}
            description={t("advanced_notification_description", { defaultValue: "Manage your notification preferences" })}
            onClick={openNotificationPrefs}
          />
        </SettingsGroup>
      </Section>

      <Section
        title={
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            {t("blocked_users_section_title", { defaultValue: "Blocked users" })}
            {blockedCount > 0 && <Badge variant="secondary">{blockedCount}</Badge>}
          </span>
        }
        description={t("blocked_users_section_description", {
          defaultValue:
            "People you've blocked can't see your profile, message you, or interact with your content. Unblock someone to restore interaction.",
        })}
      >
        <BlockedUsersList onCountChange={setBlockedCount} />
      </Section>

      <Section
        title={t("account_delete_account", { defaultValue: "Delete account" })}
        description={t("delete_account_description", {
          defaultValue: "Permanently delete your account and all associated data. This action cannot be undone.",
        })}
      >
        <Button variant="destructive-light" onClick={() => setShowDeleteModal(true)}>
          <Trash2 />
          {t("account_delete_account", { defaultValue: "Delete account" })}
        </Button>
      </Section>

      <AccountDeletionModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        isLoading={isDeletingAccount}
      />
    </>
  );
};

export default Privacy;
